import React, { useReducer, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Container, Row, Col, Input, Button } from "reactstrap";
import { defaultState, Dispatch, initialize } from "./store/store";
import { orderReducer, validateForm } from "./store/reducer";
import style from "./Order.module.scss";
import styleF from "./Field/Field.module.scss";
import {
  ProductType,
  getProductTypeName,
  Mode,
  getOptionsTickerFromFields,
  OrderStatus,
  Type,
} from "../../../utils/Order/Order";
import { Side, Combobox, Field, Numeric } from "./Field/Field";
import { Bonds, Forex, Equity, MFunds, Options, MMarket, Repo, Future } from "./Form";
import Footer from "./Footer/Footer";
import Precheck from "./Precheck/Precheck";
import { QuoteAction } from "../../../utils/Quote/Quote";
import { OrderInlineDetail } from "../..";
import OrderTotals from "./Footer/OrderTotals";
import Execution from "../Execution/Execution";
import { customFetchAssetInfo } from "./Form/commons";
import Message from "./Message";
import { handleFileInput, UserType } from "../../../utils/Helper";

const normalizeCommissionForAccount = (account) => {
  if (!account) return account;

  const normalized = { ...account };
  const commissionTypeStr = String(normalized.CommissionType || "").toLowerCase();

  if (commissionTypeStr.includes("clientprice") && normalized.ClientPrice) {
    normalized.Commission = normalized.ClientPrice;
  }

  return normalized;
};

const normalizeOrderForSend = (order) => {
  const orderToSend = { ...order };

  if (orderToSend.CurrentAccount) {
    orderToSend.CurrentAccount = normalizeCommissionForAccount(orderToSend.CurrentAccount);
  }

  if (Array.isArray(orderToSend.Accounts)) {
    orderToSend.Accounts = orderToSend.Accounts.map((account) => normalizeCommissionForAccount(account));
  }

  return orderToSend;
};

const Order = ({ open, toggle, ...props }) => {
  const [state, dispatch] = useReducer(orderReducer, defaultState(), initialize(props));
  const [message, setMessage] = useState({ open: false });
  const { ui, order, fields, buttons } = state;
  const [orderForms, setOrderForms] = useState([]);
  const [maxDecimals, setMaxDecimals] = useState(undefined);
  const [newSymbol, setNewSymbol] = useState(false);
  const [booktypes, setBooktypes] = useState([]);
  const [automaticSubAssetClasses, setAutomaticSubAssetClasses] = useState([]);

  useEffect(() => {
    if (props.getBooktypes) {
      const isTrader = ui.userRole === "Trader";
      props.getBooktypes(isTrader).then((r) => {
        if (r.response && r.response.booktypes) {
          setBooktypes(r.response.booktypes);
        }
      });
    } else {
      setBooktypes([]);
    }
  }, []);
  const { platformRole } = props;
  useEffect(() => {
    const newOrderForms = [];
    Object.values(ProductType).map((key, index) => {
      if (
        !(
          ["Bulk", "Basket", "Chained"].includes(props.type) &&
          [ProductType.REPO, ProductType.FUTURE].includes(key)
        )
      )
        newOrderForms.push({
          id: key,
          description: getProductTypeName(key),
        });
    });
    setOrderForms(newOrderForms);
  }, [props.type]);

  useEffect(() => {
    if (ui.quoteId) {
      customFetchAssetInfo(
        dispatch,
        { assetInfo: props.onOrderAssetInfo },
        {
          OrderInitDate: order.OrderInitDate,
          OrderFutureSettlmentDate: order.OrderFutureSettlmentDate,
          AssetClassForm: order.AssetClassForm,
          AssetId: order.AssetId,
          AssetTicker: order.AssetTicker,
          OrderCurrencyId: order.OrderCurrencyId,
          OrderSettlementCurrencyId: order.OrderSettlementCurrencyId,
          SubAssetClassAllowsProcessDate: order.SubAssetClassAllowsProcessDate,
        },
      );
    }
  }, [
    order.OffertoAdvisors,
    order.OffertoClients,
    order.OrderInitDate,
    order.OrderFutureSettlmentDate,
    order.AssetClassForm,
    order.AssetId,
    order.AssetTicker,
    order.OrderCurrencyId,
    order.OrderSettlementCurrencyId,
    props.onOrderAssetInfo,
    ui.quoteId,
  ]);

  const updatePrice = () => {
    setNewSymbol(true);
  };

  useEffect(() => {
    if (!ui.isBasket || !props.getParameterValue) return;

    const payload = { ParameterName: "AutomationBasketSubAC" };

    props.getParameterValue(payload, (response) => {
      if (response && response.SdtParametersData) {
        const subAssetClassesIds = response.SdtParametersData.ParameterCharacterValueLong;
        setAutomaticSubAssetClasses(subAssetClassesIds ? subAssetClassesIds.split(",") : []);
      }
    });
  }, []);

  useEffect(() => {
    if (order.SubAssetClassDecimalPlaces) {
      setMaxDecimals(order.SubAssetClassDecimalPlaces);
    } else {
      props.fetchGetAssetInformation({ assetId: order.AssetId }).then((response) => {
        setMaxDecimals(response.info.SubAssetClassDecimalPlaces);
      });
    }

    setNewSymbol(false);
    order.SubAssetClassDecimalPlaces
      ? (order.OrderPrice = Number(parseFloat(order.OrderPrice).toFixed(order.SubAssetClassDecimalPlaces)))
      : null;
  }, [order.OrderPrice, newSymbol]);

  useEffect(() => {
    const cOrder = { ...order };
    if (ui.mode == Mode.EDIT_COMMISSION && cOrder.OrderStatus == "Filled") {
      dispatch({ type: "CHANGE_BUTTON_PROPS", props: { confirm: { visible: false } } });
    }
    if (
      (ui.mode === Mode.EDIT || ui.mode == Mode.READONLY) &&
      (cOrder.OrderStatus === OrderStatus.ROUTED || cOrder.OrderStatus === OrderStatus.ACKED)
    ) {
      dispatch({ type: "CHANGE_BUTTON_PROPS", props: { save: { visible: false } } });
    }
    const canEditFields =
      Object.values(state.fields.editable).some((value) => value === true) &&
      cOrder.OrderStatus === OrderStatus.ACKED;
    if (canEditFields && ui.mode === Mode.READONLY) {
      dispatch({ type: "CHANGE_BUTTON_PROPS", props: { precheck: { visible: true } } });
    }
  }, [order]);

  // called from bulk and basket
  const canAddAccount = () => {
    const formErrors = validateForm(state);

    if (formErrors == false) {
      return true;
    }
    const { OrderQuantity, Accounts, Quantity, AccountName, HolderName, Instruction, ...errors } = formErrors;
    if (Object.keys(errors).length != 0) {
      dispatch({ type: "FORCE_UPDATE_VALIDATION", errors });
    } else {
      return true;
    }
  };

  const save = () => {
    const formErrors = validateForm(state, true);

    if (state.ui.userRole === UserType.MiddleOffice) {
      if (Object.keys(state.attachments || {}).length > 0) {
        const data = {
          orderId: state.order.OrderId,
          files: [],
        };
        Promise.all(
          Object.entries(state.attachments).map(
            ([key, value]) =>
              new Promise(async (resolve, reject) => {
                if (value) {
                  try {
                    const { blob, name, extension } = await handleFileInput(value);
                    resolve({
                      accountId: key,
                      name,
                      blob,
                      extension,
                    });
                  } catch (e) {
                    reject();
                  }
                } else {
                  reject();
                }
              }),
          ),
        ).then((files) => {
          props.onOrderAttachment({
            ...data,
            files,
          });
          toggle(order.OrderId);
        });
      }
    } else if (formErrors === false) {
      const cOrder = normalizeOrderForSend(order);

      cOrder.OrderStatus = OrderStatus.PARKED;

      if (cOrder.AssetClassForm == ProductType.OPTION) {
        cOrder.AssetTicker = getOptionsTickerFromFields(order);
      }

      dispatch({
        type: "CHANGE_BUTTON_PROPS",
        props: {
          save: { loading: true },
          precheck: { visible: false },
        },
      });

      props
        .onConfirm({ order: cOrder, isNew: !cOrder.OrderId, authorizations: state.precheck.authorizations })
        .then((res) => res.response)
        .then((res) => {
          const { result } = res;
          const { Code, Description } = result;

          if (Code !== 200) {
            dispatch({
              type: "CHANGE_BUTTON_PROPS",
              props: {
                save: { loading: false },
                precheck: { visible: false },
              },
            });
            setMessage({ open: true, message: Description });
          } else if (Object.keys(state.attachments || {}).length > 0) {
            // if order has attachments
            const data = {
              orderId: res.orderId,
              files: [],
            };
            Promise.all(
              Object.entries(state.attachments).map(
                ([key, value]) =>
                  new Promise(async (resolve, reject) => {
                    if (value) {
                      try {
                        const { blob, name, extension } = await handleFileInput(value);
                        resolve({
                          accountId: key,
                          name,
                          blob,
                          extension,
                        });
                      } catch (e) {
                        reject();
                      }
                    } else {
                      reject();
                    }
                  }),
              ),
            ).then((files) => {
              props.onOrderAttachment({
                ...data,
                files,
              });
              toggle(cOrder.OrderId);
            });
          } else {
            toggle(cOrder.OrderId);
          }
        });
    } else {
      dispatch({ type: "FORCE_UPDATE_VALIDATION", errors: formErrors });
    }
  };

  const confirmCommission = () => {
    const formErrors = validateForm(state);

    if (formErrors === false) {
      dispatch({ type: "CHANGE_BUTTON_PROPS", props: { confirm: { loading: true } } });

      /*
      const {
        CommissionType, Commission, MarkupType, Markup, FXMarkupType, FXMarkup,
      } = order.CurrentAccount;
      const newProps = {
        CommissionType, Commission, MarkupType, Markup, FXMarkupType, FXMarkup,
      };
      */

      const cOrder = normalizeOrderForSend(order);

      props
        .onConfirm({ order: cOrder, isNew: !cOrder.OrderId, authorizations: state.precheck.authorizations })
        .then((res) => {
          const { result } = res.response;

          if (result.Code !== 200) {
            dispatch({
              type: "CHANGE_BUTTON_PROPS",
              props: {
                confirm: { loading: false },
              },
            });
            setMessage({ open: true, message: result.Description });
          } else {
            toggle(order.OrderId);
          }
        });
    } else {
      dispatch({ type: "FORCE_UPDATE_VALIDATION", errors: formErrors });
    }
  };

  const saveAttachments = (orderId, attachments) => {
    if (Object.keys(attachments || {}).length > 0) {
      const data = {
        orderId,
        files: [],
      };
      Promise.all(
        Object.entries(attachments).map(
          ([key, value]) =>
            new Promise(async (resolve, reject) => {
              if (value) {
                try {
                  const { blob, name, extension } = await handleFileInput(value);
                  resolve({
                    accountId: key,
                    name,
                    blob,
                    extension,
                  });
                } catch (e) {
                  reject();
                }
              } else {
                reject();
              }
            }),
        ),
      ).then((files) => {
        props.onOrderAttachment({
          ...data,
          files,
        });
      });
    }
  };

  const onOrderUpdate =
    (inOrderId, isConfirm, isCreate = false) =>
    (res) => {
      const { ui, precheck } = state;
      const { result } = res.response;

      if (![200, 204].includes(result.Code)) {
        setMessage({ open: true, message: result.Description });
        if (isConfirm) {
          dispatch({
            type: "CHANGE_BUTTON_PROPS",
            props: { confirm: { loading: false } },
          });
        } else {
          dispatch({
            type: "CHANGE_BUTTON_PROPS",
            props: { modify: { loading: false } },
          });
        }
        return Promise.reject("Order Creation/Update failed");
      }

      const orderId = inOrderId > 0 ? inOrderId : res.response.orderId;

      // if order has attachments
      saveAttachments(orderId, state.attachments);

      // if is from quote, change quote as used
      if (ui.quoteId) {
        props.updateQuote({
          quoteAction: QuoteAction.USE,
          quote: { AdvisorQuoteId: ui.quoteId },
        });
      }

      // add authorizations histories
      props.onAddHistories({
        histories: precheck.histories.map((h) => ({
          ...h,
          HistoryRefProcessId: orderId,
        })),
      });

      if (isCreate) {
        isConfirmHandler(ui, isConfirm, orderId);
      } else {
        // create authorizations
        new Promise((resolve) => {
          const authorizations = precheck.authorizations.map((authorization) => ({
            ...authorization,
            refid: orderId,
            //secRefId: cOrder.AccountId,
          }));
          props.onValidationsCreateAuth({ authorizations }).then((resp) => resolve(resp.response));
        }).then((r) => isConfirmHandler(ui, isConfirm, orderId));
      }

      // is warning code fired from OMS
      if (result.Code == 204) {
        alert(result.Description);
      }
    };

  const isConfirmHandler = (ui, isConfirm, orderId) => {
    if (isConfirm) {
      if (ui.userRole == "Trader" && !ui.isBasket) {
        dispatch({ type: "CHANGE_MODE", mode: Mode.EXECUTE, id: orderId });
      } else {
        toggle(orderId);
      }
    } else {
      dispatch({ type: "CHANGE_MODE", mode: Mode.EDIT });
    }
  };

  const confirm = async () => {
    const { ui, precheck } = state;

    // intercept if editing commission on filled orders from advisor
    if (ui.mode == Mode.EDIT_COMMISSION) {
      confirmCommission();
      return;
    }

    // special check for bulk and basket orders
    if (ui.isBulk || ui.isBasket) {
      const formErrors = validateForm(state);

      if (formErrors != false) {
        dispatch({ type: "FORCE_UPDATE_VALIDATION", errors: formErrors });
        return;
      }
    }

    let cOrder = normalizeOrderForSend(order);

    // change ticker if is an option
    if (cOrder.AssetClassForm == ProductType.OPTION) {
      //cOrder.AssetTicker = getOptionsTickerFromFields(cOrder);
    }

    let hasErrors = false;

    // check if any authorizations has error
    const authorizations = precheck.authorizations.map((auth) => {
      if (auth.text == null || !auth.text.length) {
        hasErrors = true;
        return { ...auth, error: "Required field", readonly: undefined };
      }

      return { ...auth, error: null, readonly: undefined };
    });

    if (!hasErrors) {
      authorizations.forEach((a) => (a.readonly = true));
    }

    dispatch({ type: "UPDATE_PRECHECK", fields: { authorizations } });

    if (!hasErrors) {
      // check if all authorizations can be authorized by self
      const allSelfAuth = precheck.authorizations.every((a) => a.role.toLowerCase() === "self");

      // if allSefAuth = true then order will has ROUTED status
      if (!allSelfAuth) {
        // If not all authorizations can be authorized by self
        cOrder.OrderStatus = OrderStatus.AUTH_WAIT;
      }
      // if parked.. when confirm change to routed
      else if (cOrder.OrderStatus == OrderStatus.PARKED) {
        cOrder.OrderStatus = OrderStatus.ROUTED;
      }

      dispatch({
        type: "CHANGE_BUTTON_PROPS",
        props: { confirm: { loading: true } },
      });

      const isExistingMultiAccountOrder = cOrder.OrderId > 0 && (ui.isBulk || ui.isBasket);

      const isAutomaticBasketInWorking =
        ui.isBasket &&
        cOrder.OrderStatus == OrderStatus.ACKED &&
        automaticSubAssetClasses.includes(String(cOrder.AssetSubAssetClassId));

      if (isAutomaticBasketInWorking && props.fetchUpdateOrderRate) {
        const payload = {
          orderId: cOrder.OrderId,
          newRate: cOrder.OrderPrice,
        };

        props
          .fetchUpdateOrderRate(payload)
          .then(onOrderUpdate(cOrder.OrderId, true, true))
          .then(() => toggle(cOrder.OrderId))
          .catch();

        return;
      }

      if (ui.mode == Mode.PRECHECK && isExistingMultiAccountOrder) {
        props
          .onAccountUpdate({
            orderId: cOrder.OrderId,
            account: cOrder.CurrentAccount,
          })
          .then(onOrderUpdate(cOrder.OrderId, true))
          .then(() => toggle(cOrder.OrderId))
          .catch();
      } else {
        if (isExistingMultiAccountOrder) {
          // The accounts are updated in other procedures
          cOrder.Accounts = [];
          cOrder.CurrentAccount = {};
        }
        // update order service
        props
          .onConfirm({
            order: cOrder,
            isNew: !cOrder.OrderId,
            authorizations: precheck.authorizations,
          })
          .then(onOrderUpdate(cOrder.OrderId, true, true))
          .then(() => toggle(cOrder.OrderId))
          .catch();
      }
    }
  };

  const goBack = () => {
    dispatch({ type: "CHANGE_MODE", mode: Mode.EDIT });
  };

  const modify = async () => {
    const { ui, precheck } = state;

    const isExistingMultiAccountOrder = order.OrderId > 0 && (ui.isBulk || ui.isBasket);

    if (isExistingMultiAccountOrder) {
      const formErrors = validateForm(state);

      if (formErrors != false) {
        dispatch({ type: "FORCE_UPDATE_VALIDATION", errors: formErrors });
        return;
      }

      let hasErrors = false;

      // check if any authorizations has error
      const authorizations = precheck.authorizations.map((auth) => {
        if (auth.text == null || !auth.text.length) {
          hasErrors = true;
          return { ...auth, error: "Required field", readonly: undefined };
        }

        return { ...auth, error: null, readonly: undefined };
      });

      if (!hasErrors) {
        authorizations.forEach((a) => (a.readonly = true));
      }

      dispatch({ type: "UPDATE_PRECHECK", fields: { authorizations } });

      if (!hasErrors) {
        const currentAccount = normalizeCommissionForAccount(order.CurrentAccount);
        const cOrder = {
          ...normalizeOrderForSend(order),
          Accounts: [], // The accounts are updated individually, so are not necessary here.
          CurrentAccount: {},
        };

        // change ticker if is an option
        if (cOrder.AssetClassForm == ProductType.OPTION) {
          cOrder.AssetTicker = getOptionsTickerFromFields(cOrder);
        }

        // check if all authorizations can be authorized by self
        const allSelfAuth = precheck.authorizations.every((a) => a.role.toLowerCase() === "self");

        // if allSefAuth = true then order will has ROUTED status
        if (!allSelfAuth) {
          // If not all authorizations can be authorized by self
          cOrder.OrderStatus = OrderStatus.AUTH_WAIT;
        }
        // if parked.. when confirm change to routed
        else if (cOrder.OrderStatus == OrderStatus.PARKED) {
          cOrder.OrderStatus = OrderStatus.ROUTED;
        }

        dispatch({
          type: "CHANGE_BUTTON_PROPS",
          props: { modify: { loading: true } },
        });

        // The call to onConfirm will only update the order data.
        // Then, the onAccountUpdate will update/add the account info.
        props
          .onConfirm({
            order: cOrder,
            isNew: false,
            authorizations: precheck.authorizations,
          })
          .then((res) => {
            const { result } = res.response;

            if (result.Code !== 200) {
              try {
                onOrderUpdate(cOrder.OrderId, true)(res);
              } catch (err) {}
            } else {
              props
                .onAccountUpdate({
                  orderId: cOrder.OrderId,
                  account: currentAccount,
                })
                .then(onOrderUpdate(cOrder.OrderId, false))
                .catch();
            }
          })
          .catch()
          .finally(() => {
            dispatch({
              type: "CHANGE_BUTTON_PROPS",
              props: { modify: { loading: false } },
            });
          });
      }
    } else {
      dispatch({ type: "CHANGE_MODE", mode: Mode.EDIT });
    }
  };

  const toggleAction = async () => {
    const { ui, attachments } = state;

    const isExistingReadonlyOrder = order.OrderId > 0 && ui.mode !== Mode.EDIT;

    if (isExistingReadonlyOrder) {
      // if order has attachments
      saveAttachments(order.OrderId, attachments);
    }

    toggle(order.OrderId);
  };

  const nextStep = () => {
    const { activeStep } = state.wizzard;

    if (state.ui.mode == Mode.EDIT_COMMISSION) {
      confirmCommission();
      return;
    }

    if (activeStep != 3) {
      dispatch({ type: "WIZZ_NEXT_STEP" });
    } else {
      // confirmation with wizzard
      const { wizzard } = state;

      const context1 = wizzard.steps[0].context;
      const context2 = wizzard.steps[2].context;

      const order1 = normalizeOrderForSend({ ...context1.order });
      const order2 = normalizeOrderForSend({ ...context2.order });

      // change ticker if is an option
      if (order1.AssetClassForm == ProductType.OPTION) {
        order1.AssetTicker = getOptionsTickerFromFields(order1);
      }

      if (order2.AssetClassForm == ProductType.OPTION) {
        order2.AssetTicker = getOptionsTickerFromFields(order2);
      }

      dispatch({ type: "CHANGE_BUTTON_PROPS", props: { confirm: { loading: true } } });

      props
        .onConfirm({
          order: order1,
          order2,
          isNew: true,
          authorizations: state.precheck.authorizations,
        })
        .then((res) => {
          const { response } = res;

          props.onAddHistories({
            histories: context1.precheck.histories.map((h) => ({
              ...h,
              HistoryRefProcessId: response.orderId,
            })),
          });

          props.onAddHistories({
            histories: context2.precheck.histories.map((h) => ({
              ...h,
              HistoryRefProcessId: response.orderId2,
            })),
          });

          setTimeout(() => toggle(response.orderId), 250);
        });
    }
  };

  return (
    <Dispatch.Provider
      value={{
        services: services({ ...props, canAddAccount }),
        state,
        dispatch,
      }}
    >
      <Modal isOpen={open} className={style.modal}>
        <ModalHeader toggle={() => toggle(order.OrderId)}>
          {ui.title}
          {(ui.mode === Mode.PRECHECK || ui.mode === Mode.CONFIRM || ui.mode === Mode.EXECUTE) && (
            <OrderInlineDetail order={order} hideAvg />
          )}
        </ModalHeader>
        <ModalBody className={style.body}>
          {ui.mode == Mode.EXECUTE && (
            <Execution
              open
              onToggle={() => toggle(order.OrderId)}
              entities={props.entities}
              order={order}
              orderId={order.OrderId}
              services={{
                fetchAddExecution: props.onAddExecution,
                fetchRemoveExecution: () => {},
                fetchTradingRoutes: props.onGetTradingRoutes,
                fetchOrderAssetInfo: props.onOrderAssetInfo,
                fetchGetAssetInformation: props.fetchGetAssetInformation,
                getTradesTimeSpan: props.getTradesTimeSpan,
                fetchGetAssetQuote: props.fetchGetAssetQuote,
                getAuthorizedTraders: props.getAuthorizedTraders,
                fetchOwnPositionAccounts: props.fetchOwnPositionAccounts,
              }}
            />
          )}
          {(ui.mode == Mode.EDIT ||
            ui.mode == Mode.READONLY ||
            ui.mode == Mode.EDIT_COMMISSION ||
            ui.mode == Mode.REALLOCATE) && (
            <React.Fragment>
              <Container>
                <Row>
                  <Col lg={6} xs={12}>
                    <Row style={{ marginTop: "12px", marginBottom: "10px" }}>
                      <Col lg={6} xs={12}>
                        <Side
                          orderForm={order.AssetClassForm}
                          side={order.OrderSide}
                          openClose={order.OrderOpenClose}
                          withOpenClose={order.AssetClassForm == ProductType.OPTION}
                          readonly={fields.readonly.OrderSide}
                          onChange={(v) => onChange(dispatch, v)}
                        />
                      </Col>
                      <Col lg={6} xs={12}>
                        <Combobox
                          style={{
                            background: "#607d8b",
                            color: "#fff",
                            fontWeight: 500,
                          }}
                          disabled={fields.readonly.AssetClassForm}
                          onChange={(v) => onChangeForm(dispatch, v)}
                          value={order.AssetClassForm}
                          items={orderForms}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>

              {/* Dynamic form */}
              {getForm(
                order.AssetClassForm,
                maxDecimals,
                updatePrice,
                newSymbol,
                props.fetchCalculatorDataForOffers,
                booktypes,
                platformRole,
                props.fetchGetCalculator,
                props.opsViewAccountName,
                props.fetchOrderSettlementDays,
                props.GetDayFundProcessDate,
                toggle,
              )}

              <Container>
                {[OrderStatus.PART, OrderStatus.PART_FILLED, OrderStatus.FILLED].includes(
                  order.OrderStatus,
                ) &&
                  (ui.userRole == "Advisor" || order.AccountType != "Customer") && (
                    <OrderTotals
                      fetchTotals={props.onTotals}
                      orderId={order.OrderId}
                      decimalScale={maxDecimals}
                    />
                  )}
                <Row>
                  <Col lg={12}>
                    <Input
                      className={styleF.field}
                      type="textarea"
                      rows={2}
                      placeholder="Notes..."
                      value={order.OrderNote}
                      disabled={fields.readonly.OrderNote}
                      style={{ marginTop: "5px" }}
                      onChange={(e) => onChange(dispatch, { OrderNote: e.target.value })}
                    />
                  </Col>
                </Row>
              </Container>
            </React.Fragment>
          )}

          {(ui.mode == Mode.CONFIRM || ui.mode == Mode.PRECHECK) && (
            <Precheck
              order={order}
              precheck={state.precheck}
              dispatch={(e) => dispatch(e)}
              actions={{
                ...services(props).precheck,
              }}
            />
          )}
          {message.open && <Message {...message} toggle={() => setMessage({ open: false })} />}
        </ModalBody>
        <ModalFooter>
          <Footer
            side={order.OrderSide}
            buttons={buttons}
            mode={ui.mode}
            wizzard={{
              enabled: state.wizzard.enabled,
              activeStep: state.wizzard.activeStep,
              nextBtn: state.wizzard.nextBtn,
            }}
            actions={{
              save,
              modify,
              confirm,
              nextStep,
              toggle: toggleAction,
              goBack,
            }}
          />
        </ModalFooter>
      </Modal>
    </Dispatch.Provider>
  );
};

const services = (props) => ({
  account: {
    searchAccount: props.onSearchAccount,
    downloadAttachment: props.onOrderDownloadAttachment,
    accountHolders: props.onAccountHolders,
    getUserExternalAccounts: props.getUserExternalAccounts,
    getParameterValue: props.getParameterValue,
    imgSource: props.imageSource,
    orderPdf: props.onOrderPdf,
    accountInfo: props.onOrderAccountInfo,
    commissionInfo: props.onOrderCommissionInfo,
    canAddAccount: props.canAddAccount,
    resendAuth: props.resendAuth,
    removeAccount: props.onAccountRemove,
    getOffer: props.onGetOffer,
    fetchGetOrder: props.fetchGetOrder,
    fetchOwnPositionAccounts: props.fetchOwnPositionAccounts,
    ManageCustomerExternalAccount: props.ManageCustomerExternalAccount,
    fetchGetNextValidWithParameters: props.fetchGetNextValidWithParameters,
    fetchRecalculatePriceWithProcessDate: props.fetchRecalculatePriceWithProcessDate,
    getAccountsFromXLSX: props.getAccountsFromXLSX,
  },
  symbol: {
    searchSymbol: props.onSearchSymbol,
    searchRepoSymbol: props.onSearchRepoSymbol,
    getYahooOptions: props.onGetYahooOptions,
    assetInfo: props.onOrderAssetInfo,
    assetInfromation: props.fetchGetAssetInformation,
  },
  precheck: {
    getRiskValidations: props.onRiskValidations,
    getTotalsAndEarnings: props.onTotalsAndEarnings,
    validateRule: props.onValidationsCheckRule,
    checkAuth: props.onValidationsCheckAuth,
  },
});

const getForm = (
  form,
  maxDecimals,
  updatePrice,
  newSymbol,
  fetchCalculatorDataForOffers,
  booktypes,
  platformRole,
  fetchGetCalculator,
  opsViewAccountName,
  fetchOrderSettlementDays,
  GetDayFundProcessDate,
  toggle,
) => {
  switch (form) {
    case ProductType.BOND:
      return (
        <Bonds
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          fetchCalculatorDataForOffers={fetchCalculatorDataForOffers}
          booktypes={booktypes}
          platformRole={platformRole}
          fetchGetCalculator={fetchGetCalculator}
          opsViewAccountName={opsViewAccountName}
          toggle={toggle}
        />
      );
    case ProductType.EQUITY:
      return (
        <Equity
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
      return (
        <Equity
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
    case ProductType.OPTION:
      return (
        <Options
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
    case ProductType.MFUNDS:
      return (
        <MFunds
          booktypes={booktypes}
          platformRole={platformRole}
          opsViewAccountName={opsViewAccountName}
          fetchOrderSettlementDays={fetchOrderSettlementDays}
          GetDayFundProcessDate={GetDayFundProcessDate}
        />
      );
    case ProductType.MMARKET:
      return (
        <MMarket
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
    case ProductType.FOREX:
      return (
        <Forex
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
    case ProductType.REPO:
      return (
        <Repo
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          booktypes={booktypes}
          platformRole={platformRole}
          opsViewAccountName={opsViewAccountName}
        />
      );
    case ProductType.FUTURE:
      return (
        <Future
          maxDecimals={maxDecimals}
          updatePrice={updatePrice}
          newSymbol={newSymbol}
          platformRole={platformRole}
          booktypes={booktypes}
          opsViewAccountName={opsViewAccountName}
        />
      );
  }
};

const onChangeForm = (dispatch, newForm) => dispatch({ type: "CHANGE_FORM", newForm });

export const onChange = (dispatch, fields) => {
  dispatch({ type: "CHANGE_ORDER_FIELDS", fields });
};

export const onAccountChange = (dispatch, fields) => {
  dispatch({ type: "CHANGE_ORDER_ACCOUNT_FIELDS", fields });
};

export default Order;
